<template>
  <div class="date-time-duration-picker d-flex" style="gap: 10px">
    <date-picker
      v-model="date"
      :readonly="readonly"
      :min="minDate"
      :max="maxDate"
      :variant="variant"
      :date-disabled-fn="dateDisabledFn"
      :date-format-options="{
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      }"
      locale="en-US"
      style="width: 178px"
      calendar-width="230px"
      hide-header
    />
    <b-dropdown
      :disabled="disabled"
      :variant="variant"
      :no-flip="true"
      :popper-opts="{ positionFixed: true }"
      class="border rounded"
      style="width: 124px"
      no-caret
    >
      <template slot="button-content">
        <div class="d-flex align-items-center">
          <div style="font-size: 13px; margin-right: 6px;">
            <i
              class="fa fa-clock-o color-custom-black"
              style="font-size: 20px;"
              aria-hidden="true"
            ></i>
          </div>
          <span :style="timeLabel ? '' : 'color: #909db8;'">{{
            timeLabel ? timeLabel : "HH:mm"
          }}</span>
        </div>
      </template>
      <b-dropdown-form class="px-2">
        <div class="d-flex flex-column justify-content-center">
          <div
            v-if="true"
            class="d-flex justify-content-start align-items-center mb-2"
          >
            <vue-toggles
              @click="allDay = !allDay"
              :value="allDay"
              height="30"
              width="50"
              checkedText=""
              uncheckedText=""
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
            />
            <span class="ml-2">All Day</span>
          </div>
          <b-time
            v-model="time"
            :readonly="readonly || allDay"
            locale="en"
            hide-header
          />
        </div>
      </b-dropdown-form>
    </b-dropdown>
    <div class="d-flex align-items-center ml-3" v-if="!allDay">
      <b-form-input
        v-model="duration"
        type="number"
        style="width: 70px; height: 100%"
        min="1"
      />
      <span class="ml-2">min</span>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "./DatePicker.vue";

export default {
  name: "DateTimeDurationPicker",
  components: { DatePicker },
  props: {
    value: {
      type: Object
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    minDate: {
      type: [Date, String],
      default: null
    },
    maxDate: {
      type: [Date, String],
      default: null
    },
    dateDisabledFn: {
      type: Function,
      default: null
    },
    showAllDay: {
      type: Boolean,
      default: false
    },
    beginTime: {
      type: String,
      default: "00:00:00"
    },
    endTime: {
      type: String,
      default: "23:59:59"
    }
  },
  computed: {
    date: {
      get() {
        return this.value?.date;
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          date: newValue
        });
      }
    },
    time: {
      get() {
        return this.value?.time;
      },
      set(newValue) {
        const emit = value => {
          this.$emit("input", {
            ...this.value,
            time: value
          });
        };

        if (!this.beginTime || !this.endTime) {
          emit(newValue);
          return;
        }

        const time = moment(newValue, "HH:mm:ss");

        const beginTime = moment(this.beginTime, "HH:mm:ss");
        const endTime = moment(this.endTime, "HH:mm:ss");

        if (time.isBefore(beginTime)) {
          const diff = beginTime.diff(time);

          if (diff < 3600000 && endTime.minutes() === 0) {
            endTime.subtract(diff);
          }

          emit(endTime.format("HH:mm:ss"));
        } else if (time.isAfter(endTime)) {
          const diff = endTime.diff(time);

          if (diff > -3600000 && beginTime.minutes() === 0) {
            beginTime.subtract(diff);
          }

          emit(beginTime.format("HH:mm:ss"));
        } else {
          emit(time.format("HH:mm:ss"));
        }
      }
    },
    duration: {
      get() {
        return this.value?.duration;
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          duration: newValue
        });
      }
    },
    allDay: {
      get() {
        return this.value?.allDay;
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          allDay: newValue,
          duration: newValue ? null : 30
        });
      }
    },
    timeLabel() {
      if (this.allDay) {
        return "All day";
      } else if (this.time) {
        return this.formatTimeOnly(this.time, "HH:mm:ss");
      } else {
        return "";
      }
    },
    variant() {
      return this.errors.length ? "outline-danger" : "text";
    }
  },
  watch: {
    duration(newVal) {
      if (newVal < 1) {
        this.duration = 1;
      }
    }
  }
};
</script>
<style scoped>
.date-time-duration-picker >>> .dropdown-toggle {
  padding: 0.375rem 0.75rem;
}
.date-time-duration-picker >>> .b-form-btn-label-control.form-control {
  align-items: center;
}
</style>
